import { BaseViewStore } from 'core/stores';
import { action, observable } from "mobx";
import { FacilityOrderService, FacilityOrderTemplateService, FacilityOrderTemplateItemService } from "application/facility-order/services";
import { ModalParams } from 'core/models';
import _ from 'lodash';
import { LookupService } from "common/services";

class FacilityOrderNewFlowNonScheduledTubesModalStore extends BaseViewStore {
    @observable accessor items;
    @observable accessor isLoading = false;
    @observable accessor isSaveLoading = false;
    @observable accessor isNotCurrentTubeAdded = false;
    constructor(rootStore, componentProps) {
        super(rootStore);

        this.rootStore = rootStore;
        this.modalParams = componentProps.modalParams;
        this.sendOrder = this.modalParams.data.sendOrder;
        this.facilityOrderId = this.modalParams.data.facilityOrderId;
        this.fetchData = this.modalParams.data.fetchData;
        this.disabledSendOrder = this.modalParams.data.disabledSendOrder;

        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateItemService = new FacilityOrderTemplateItemService(rootStore.application.baasic.apiClient);

        this.addTubeConfirmModal = new ModalParams({ onClose: null });
        this.addTubeConfirmModal.onConfirm = async () => { this.addTubeConfirmModal.close(); await this.includeNotCurrentTube(); };
        this.addTubeConfirmModal.onCancel = () => { this.addTubeConfirmModal.close() };
        this.addTubeConfirmModal.yesLabel = 'MODAL.ADD_TUBES_NOW_LABEL';
        this.addTubeConfirmModal.noLabel = 'MODAL.CANCEL_LABEL';
        this.addTubeConfirmModal.swapButtons = true;

        this.fetch([this.initialize()]);
    }

    @action
    async initialize() {
        this.facilityOrder = (await this.facilityOrderService.get(this.facilityOrderId, { embed: "Facility,Facility.FacilityAreaOfCares" })).data;
        this.isNotCurrentTubeAdded = this.facilityOrder.isNotCurrentTubeAdded;

        const hideGTube = this.facilityOrder.facility && this.facilityOrder.facility.facilityAreaOfCares && this.facilityOrder.facility.facilityAreaOfCares.some(faoc => faoc.areaOfCare && faoc.areaOfCare.abrv === "enteral" && faoc.coverageType && faoc.coverageType.abrv === "coveredOnly");
        const hideTrachTube = this.facilityOrder.facility && this.facilityOrder.facility.facilityAreaOfCares && this.facilityOrder.facility.facilityAreaOfCares.some(faoc => faoc.areaOfCare && faoc.areaOfCare.abrv === "respiratory" && faoc.coverageType && faoc.coverageType.abrv === "coveredOnly");
        if (hideGTube && hideTrachTube) {
            return;
        }

        const lookupService = new LookupService(this.rootStore.application.baasic.apiClient);
        const lookups = await lookupService.getLookups("ItemCategory");

        let params = {
            pageSize: 10000,
            facilityOrderId: this.facilityOrder.id,
            embed: 'FacilityOrderTemplateItems,Patient,SubCategory',
            notCurrentTube: true,
            orderBy: "SubCategory",
            orderDirection: "asc",
            inActive: false,
            itemCategoryIds: this.facilityOrder.isEnteralWeek ? [lookups.itemCategory.find(x => x.abrv == 'enteral').id] : undefined,
        }

        const response = await this.facilityOrderTemplateService.find(params);
        this.items = response.data.item && response.data.item.filter(i => i.facilityOrderTemplateItems.some(x => x.importAction.abrv != 'delete' && x.orderQuantity) && ((i.subCategory.abrv == "G-Tube" && !hideGTube) || (i.subCategory.abrv == "Trach-Tube" && !hideTrachTube))).flatMap(i =>
            i.facilityOrderTemplateItems.filter(x => x.importAction.abrv != 'delete' && x.orderQuantity).map(x => ({
                id: x.id,
                facilityOrderTemplateId: x.facilityOrderTemplateId,
                includeNotCurrentTube: this.isNotCurrentTubeAdded ? x.includeNotCurrentTube : _.isNull(x.includeNotCurrentTube) ? i.subCategory.abrv == "Trach-Tube" && this.facilityOrder.facility.isMonthlyTrachTube : x.includeNotCurrentTube,
                patientName: `${i.patient.lastName}, ${i.patient.firstName}`,
                description: x.product.description,
                itemId: x.product.itemId,
                nextRunDate: i.nextRunDate
            }))
        );
    }

    @action.bound
    async confirmTubes() {
        if (this.sendOrder) {
            await this.includeNotCurrentTube();
        }
        else {
            if (!this.items || !this.items.some(x => x.includeNotCurrentTube)) {
                this.modalParams.close();
            }
            else {
                this.addTubeConfirmModal.open({
                    message: 'The system will not update the "above allowable" template if changes are made to the order.',
                    title: 'Add Tubes Now?'
                });
            }
        }
    }

    @action.bound
    async includeNotCurrentTube() {
        try {
            this.isLoading = true;
            if (this.items) {
                var response = await this.facilityOrderTemplateService.createNotCurrentTubeExtraSupplyTemplate(this.items);
                if (response.data.success) {
                    if (this.items.some(x => x.includeNotCurrentTube)) {
                        this.rootStore.notificationStore.success('The "above allowable" template successfully created.');
                    }

                    if (!this.sendOrder && this.fetchData) {
                        await this.fetchData();
                    }
                }
                else {
                    this.rootStore.notificationStore.error(response.data.errors);
                }
            }

            if (this.sendOrder) {
                await this.sendOrder();
                if (this.fetchData) {
                    await this.fetchData();
                }
            }

            this.modalParams.close();
        }
        catch (err) {
            this.isLoading = false;
            this.rootStore.notificationStore.error('Error occurred while trying to crate "above allowable" template');
        }
    }

    @action.bound
    async setIncludeNotCurrentTube(item) {
        item.includeNotCurrentTube = !item.includeNotCurrentTube;
    }

    @action.bound
    async saveTubes() {
        try {
            this.isSaveLoading = true;
            await this.facilityOrderTemplateItemService.updateIncludeNotCurrentTube(this.items.map(i => { return { id: i.id, includeNotCurrentTube: !!i.includeNotCurrentTube } }));
            this.rootStore.notificationStore.success('The "above allowable" template will be created when the order is sent and the tubes are confirmed.');
            this.modalParams.close();
        }
        catch (err) {
            this.isSaveLoading = false;
            this.rootStore.notificationStore.error("Error occured while saving.");
        }
    }
}

export default FacilityOrderNewFlowNonScheduledTubesModalStore;
